import type {
  CircleLayerSpecification,
  ExpressionSpecification,
  VectorSourceSpecification,
} from 'maplibre-gl';

export const INDUSTRIAL_REF_BOOK_SOURCE_ID = 'industrial-ref-book';
export const INDUSTRIAL_REF_BOOK_SOURCE_LAYER = 'industrial_ref_book';
export const INDUSTRIAL_REF_BOOK_LAYER_ID = 'industrial-ref-book-points';
export const INDUSTRIAL_REF_BOOK_HOVERED_LAYER_ID = 'industrial-ref-book-hovered';

/**
 * Per-type colour palette. Must stay in sync with the legend swatch
 * in `MapLegend.tsx` (uses the LOCATION blue as the layer marker).
 */
export const INDUSTRIAL_REF_BOOK_COLOR_LOCATION = '#1677ff';
export const INDUSTRIAL_REF_BOOK_COLOR_AGRO_POINT = '#fa8c16';
export const INDUSTRIAL_REF_BOOK_COLOR_TERMINAL = '#722ed1';

const COLOR_BY_TYPE: ExpressionSpecification = [
  'match', ['get', 'type'],
  'LOCATION', INDUSTRIAL_REF_BOOK_COLOR_LOCATION,
  'AGRO_POINT', INDUSTRIAL_REF_BOOK_COLOR_AGRO_POINT,
  'TERMINAL', INDUSTRIAL_REF_BOOK_COLOR_TERMINAL,
  /* default */ '#8c8c8c',
];

const RADIUS: ExpressionSpecification = [
  'interpolate', ['linear'], ['zoom'],
  4, 2,
  8, 3,
  12, 5,
  16, 7,
];

export function buildIndustrialRefBookSource(
  apiBaseUrl: string,
): VectorSourceSpecification {
  return {
    type: 'vector',
    tiles: [`${apiBaseUrl}/api/v1/tiles/industrial-ref-book/{z}/{x}/{y}.mvt`],
    minzoom: 0,
    maxzoom: 22,
  };
}

export function buildIndustrialRefBookLayer(): CircleLayerSpecification {
  return {
    id: INDUSTRIAL_REF_BOOK_LAYER_ID,
    type: 'circle',
    source: INDUSTRIAL_REF_BOOK_SOURCE_ID,
    'source-layer': INDUSTRIAL_REF_BOOK_SOURCE_LAYER,
    paint: {
      'circle-radius': RADIUS,
      'circle-color': COLOR_BY_TYPE,
      'circle-stroke-color': '#ffffff',
      'circle-stroke-width': 1,
      'circle-opacity': 0.9,
    },
  };
}

const HOVERED_RADIUS: ExpressionSpecification = [
  'interpolate', ['linear'], ['zoom'],
  4, 4,
  8, 5.5,
  12, 7.5,
  16, 10,
];

/**
 * Slightly larger circle with thicker stroke, drawn on top of the
 * base layer when the user hovers a feature. The filter is updated
 * by `MainMap` from `mouseenter`/`mouseleave` events.
 */
export function buildIndustrialRefBookHoveredLayer(): CircleLayerSpecification {
  return {
    id: INDUSTRIAL_REF_BOOK_HOVERED_LAYER_ID,
    type: 'circle',
    source: INDUSTRIAL_REF_BOOK_SOURCE_ID,
    'source-layer': INDUSTRIAL_REF_BOOK_SOURCE_LAYER,
    filter: ['==', ['get', 'id'], -1],
    paint: {
      'circle-radius': HOVERED_RADIUS,
      'circle-color': COLOR_BY_TYPE,
      'circle-stroke-color': '#ffffff',
      'circle-stroke-width': 2.5,
      'circle-opacity': 1,
    },
  };
}
